import ShoppingListContainer from "@/components/ShoppingList/ShoppingListContainer";
import CategoryIndex from "@/src/categories/components/CategoryIndex";
import getCategoryIndexPageProps from "@/src/categories/lib/getCategoryIndexPageStaticProps";
import type CategoryGroup from "@/src/categories/types/CategoryGroup";
import createBreadcrumbsJsonLd from "@/src/recipes/lib/createBreadcrumbsJsonLd";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import { Seomatic } from "react-seomatic";

/*
	This page is not designed to be accessed directly.
	The page uses a category group to define which group to use (e.g. cuisines, ingredients, occasions, meals, diets, types, recipes/categories), and is re-written via Next. Please refer to next.config.ts to review the rewrites.
*/

export const getStaticPaths: GetStaticPaths = async () => {
  const groups: CategoryGroup[] = [
    "cuisines",
    "ingredients",
    "occasions",
    "meals",
    "dietaryRequirements",
    "types",
    "recipeCategories",
  ];

  return {
    paths: [...groups.map((group) => ({ params: { group } }))],
    fallback: "blocking",
  };
};

type Props = {
  categories: {
    [key: string]: any;
  }[];
  pageCount: number;
  group: CategoryGroup;
};

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const group = params.group as CategoryGroup;

  // Fetch categories by group
  const categoryIndexPageProps = await getCategoryIndexPageProps({
    group,
  });

  const breadcrumbs = [
    {
      label: group.charAt(0).toUpperCase() + group.slice(1),
      href: `/${group}`,
    },
  ];

  const jsonLdSchema = [createBreadcrumbsJsonLd(breadcrumbs)];

  return {
    props: {
      ...categoryIndexPageProps.props,
      breadcrumbs,
      jsonLdSchema,
    },
  };
};

const CategoryIndexPage = ({ categories, pageCount, pageEntry }) => {
  return (
    <>
      <Seomatic Head={Head} {...pageEntry.seomatic} />
      <ShoppingListContainer />

      <CategoryIndex
        pageEntry={pageEntry}
        heading={pageEntry.heading || pageEntry.title}
        summary={pageEntry.summary}
        categories={categories}
        pageNumber={1}
        pageCount={pageCount}
      />
    </>
  );
};

export default CategoryIndexPage;
